// ===== POC Cuidados Paliativos (PNCP) - documento das diretivas antecipadas =====
// Gera, só no front, uma via para imprimir e assinar (ou salvar em PDF pela
// janela de impressão do navegador) a partir do plano de cuidados na tela.

import moment from "moment";
import {
  DIRECTIVE_PARTICIPANTS,
  PLACE_OPTIONS,
  SUPPORT_DECISIONS,
  SUPPORT_MEASURES
} from "./carePlanApi";

const BLANK = "________________________________";

function esc(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function nameOf(options, id) {
  const option = options.find((o) => o._id === id);
  return option ? option.name : "";
}

function field(label, value) {
  return `<div class="field"><span class="label">${esc(label)}</span><span class="value">${value ? esc(value) : "—"}</span></div>`;
}

function paragraph(text) {
  return text && text.trim()
    ? `<p class="text">${esc(text.trim()).replace(/\n/g, "<br>")}</p>`
    : "<p class=\"text muted\">Não informado.</p>";
}

function buildHtml(plan, patient) {
  const directive = plan.directive || {};
  const participants = (directive.participants || [])
    .map((id) => nameOf(DIRECTIVE_PARTICIPANTS, id))
    .filter(Boolean)
    .join(", ");

  let couldDecide = "";
  if (directive.patientCouldDecide === true) couldDecide = "Sim";
  else if (directive.patientCouldDecide === false) couldDecide = "Não";

  const proxy = [directive.proxyName, directive.proxyRelation].filter(Boolean).join(" · ");
  const versions = (directive.versions || []).length;

  const measures = SUPPORT_MEASURES.map((m) => {
    const decision = (plan.supportMeasures || {})[m._id];
    return `<tr><td>${esc(m.name)}</td><td class="decision is-${esc(decision || "none")}">${esc(nameOf(SUPPORT_DECISIONS, decision) || "Não definido")}</td></tr>`;
  }).join("");

  const signatureName = (name) => (name ? esc(name) : BLANK);

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="utf-8">
<title>Diretivas antecipadas de vontade${patient.name ? ` - ${esc(patient.name)}` : ""}</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 12px; line-height: 1.45; color: #222; }
  h1 { margin: 0 0 4px; font-size: 18px; color: #4f3f82; }
  h2 { margin: 18px 0 8px; padding-bottom: 4px; border-bottom: 1px solid #ccc; font-size: 13px; text-transform: uppercase; letter-spacing: .03em; color: #4f3f82; }
  .subtitle { margin: 0 0 12px; color: #555; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; }
  .field { display: flex; flex-direction: column; }
  .label { font-size: 10px; text-transform: uppercase; color: #666; }
  .value { font-size: 12px; font-weight: bold; }
  .text { margin: 0; }
  .muted { color: #777; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 5px 8px; border: 1px solid #ccc; }
  td.decision { width: 30%; font-weight: bold; text-align: center; }
  .is-sim { color: #1e6b3a; }
  .is-nao { color: #9b1c1c; }
  .is-discutir { color: #8a5a00; }
  .legal { margin-top: 16px; padding: 8px 10px; border-left: 3px solid #4f3f82; background: #f5f3fa; font-size: 11px; }
  .signatures { display: grid; grid-template-columns: 1fr 1fr; gap: 40px 32px; margin-top: 48px; }
  .signature { padding-top: 6px; border-top: 1px solid #333; text-align: center; font-size: 11px; }
  .signature small { display: block; color: #666; }
  .footer { margin-top: 24px; font-size: 10px; color: #777; text-align: right; }
  section { break-inside: avoid; }
</style>
</head>
<body>
  <h1>Diretivas antecipadas de vontade</h1>
  <p class="subtitle">Registro da conversa sobre os cuidados que o paciente aceita ou não receber.</p>

  <section>
    <h2>Paciente</h2>
    <div class="grid">
      ${field("Nome", patient.name)}
      ${field("Data de nascimento", patient.birthDate)}
      ${field("CPF", patient.cpf)}
      ${field("CNS", patient.cns)}
    </div>
  </section>

  <section>
    <h2>Conversa</h2>
    <div class="grid">
      ${field("Data da conversa", plan.directiveDate)}
      ${field("Médico responsável", directive.doctorName)}
      ${field("Quem participou", participants)}
      ${field("O paciente tinha condição de decidir?", couldDecide)}
      ${directive.patientCouldDecide === false ? field("Quem decidiu por ele", directive.decidedBy) : ""}
      ${field("Representante indicado pelo paciente", proxy)}
    </div>
  </section>

  <section>
    <h2>Medidas de suporte</h2>
    <table><tbody>${measures}</tbody></table>
  </section>

  <section>
    <h2>Preferências de local</h2>
    <div class="grid">
      ${field("Onde prefere ser cuidado", nameOf(PLACE_OPTIONS, plan.carePlace))}
      ${field("Onde prefere estar no fim da vida", nameOf(PLACE_OPTIONS, plan.deathPlace))}
    </div>
  </section>

  <section>
    <h2>Objetivos de cuidado</h2>
    ${paragraph(plan.goals)}
  </section>

  <section>
    <h2>Valores e desejos</h2>
    ${paragraph(directive.wishes)}
  </section>

  <p class="legal">
    Registro feito pelo médico responsável conforme a Resolução CFM nº 1.995/2012. As diretivas podem ser
    revistas a qualquer momento pelo paciente; a versão válida é a mais recente registrada no prontuário.
  </p>

  <div class="signatures">
    <div class="signature">${signatureName(directive.patientCouldDecide === false ? directive.decidedBy : patient.name)}<small>${directive.patientCouldDecide === false ? "Responsável pela decisão" : "Paciente"}</small></div>
    <div class="signature">${signatureName(directive.doctorName)}<small>Médico responsável · CRM</small></div>
    <div class="signature">${signatureName(proxy ? directive.proxyName : "")}<small>Representante indicado</small></div>
    <div class="signature">${BLANK}<small>Testemunha</small></div>
  </div>

  <p class="footer">
    Gerado em ${esc(moment().format("DD/MM/YYYY [às] HH:mm"))}${versions ? ` · versão ${versions} das diretivas` : ""}
  </p>
</body>
</html>`;
}

// Imprime por um iframe oculto (não é barrado como pop-up).
export function printDirectiveDocument(plan, patient = {}) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(buildHtml(plan, patient));
  doc.close();

  const win = iframe.contentWindow;
  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  win.onafterprint = cleanup;
  setTimeout(() => {
    win.focus();
    win.print();
    // Navegadores sem onafterprint: remove depois de um tempo.
    setTimeout(cleanup, 60000);
  }, 100);
}
